import React, { useEffect } from 'react';
import { ChatHistoryResponse } from '../../services/ChatService';

interface ChatModalProps {
    chat: ChatHistoryResponse | null;
    onClose: () => void;
}

const ChatModal: React.FC<ChatModalProps> = ({ chat, onClose }) => {
    useEffect(() => {
        const handleEscKey = (event: KeyboardEvent) => {
            if (event.key === 'Escape') onClose();
        };
        window.addEventListener('keydown', handleEscKey);
        return () => window.removeEventListener('keydown', handleEscKey);
    }, [onClose]);

    if (!chat) return null;

    const infoRowClass = "flex mb-4 pb-4 border-b border-[#e2e8f0] last:mb-0 last:pb-0 last:border-0";
    const labelClass = "font-semibold text-[#4a5568] w-[120px] shrink-0";
    const valueClass = "text-[#2d3748] grow break-words";

    const fmtDate = (d: string) => new Date(d).toLocaleString('pt-BR', {
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', second: '2-digit'
    });

    return (
        <div
            className="fixed inset-0 z-[1000] flex items-center justify-center"
            style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
            onClick={onClose}
        >
            <div
                className="bg-white rounded-lg w-[90%] max-w-xl max-h-[90vh] overflow-y-auto shadow-md"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-[#e2e8f0]">
                    <h2 className="m-0 text-2xl text-[#2d3748] font-semibold">Detalhes do Chat</h2>
                    <button
                        className="bg-transparent border-none text-2xl cursor-pointer text-[#718096] p-1 leading-none hover:text-[#2d3748] transition-colors"
                        onClick={onClose}
                    >
                        ×
                    </button>
                </div>

                {/* Content */}
                <div className="p-6">
                    {[
                        { label: 'ID:', value: chat.id },
                        { label: 'Campanha:', value: chat.campaign_id },
                        { label: 'Pergunta:', value: chat.question },
                        { label: 'Resposta:', value: chat.answer },
                        { label: 'ID do Usuário:', value: chat.user_id },
                        { label: 'Tokens Utilizados:', value: chat.token_usage },
                        { label: 'Criado em:', value: fmtDate(chat.created_at) },
                        { label: 'Atualizado em:', value: fmtDate(chat.updated_at) },
                    ].map(({ label, value }) => (
                        <div key={label} className={infoRowClass}>
                            <span className={labelClass}>{label}</span>
                            <span className={valueClass}>{value}</span>
                        </div>
                    ))}

                    {chat.contexts && chat.contexts.length > 0 && (
                        <>
                            <h3 className="mt-4 mb-2 text-[#2d3748] font-semibold">Contextos Utilizados</h3>
                            {chat.contexts.map((context, index) => (
                                <div key={index} className="mb-4 border-t border-[#e2e8f0] pt-3">
                                    {[
                                        { label: 'ID do Contexto:', value: context.id },
                                        { label: 'ID do Chat:', value: context.chat_id },
                                        { label: 'Pergunta:', value: context.question },
                                        { label: 'Versão da Pergunta:', value: context.question_version },
                                        { label: 'Score:', value: context.score || 'N/A' },
                                        { label: 'Data de Criação:', value: fmtDate(context.created_at) },
                                    ].map(({ label, value }) => (
                                        <div key={label} className={infoRowClass}>
                                            <span className={labelClass}>{label}</span>
                                            <span className={valueClass}>{value}</span>
                                        </div>
                                    ))}
                                </div>
                            ))}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ChatModal;